import React from 'react';
import HBRadioButton, {
    IRadioButtonBase
} from '../HBRadioButton/HBRadioButton';
import HBField from '../HBField/HBField';
import styled from 'styled-components';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { isAriaInvalid } from '../common/internal/isAriaInvalid';
import { useGroupFocusRedirect } from '../common/internal/useGroupFocusRedirect';

export type TRadioButtonGroupOption<Value extends string | number = string> = {
    label: string;
    value: Value;
    disabled?: boolean;
};

interface _HBRadioButtonGroupProps<Value extends string | number> {
    /** The available radio button options, each with a value, label, and optional disabled flag. */
    options: TRadioButtonGroupOption<Value>[];
    /** The currently selected option value; must match one of the option values. */
    value: Value;
    /** Called when the user selects a different option. Receives a native change event. */
    onChange: IRadioButtonBase['onChange'];
    /** HTML name attribute applied to all radio inputs; required for native radio button grouping. */
    name: string;
    /** Pixel gap between radio button options; overrides the default size-based spacing (8px for sm/md, 12px for lg). */
    gap?: number;
    /** Layout direction of the radio button options. */
    alignment?: 'horizontal' | 'vertical';
    /** Size of the radio button controls and labels. Also determines default gap (8px for sm/md, 12px for lg). Defaults to 'md'. */
    size?: IRadioButtonBase['size'];
    /** Custom color theme applied to all radio buttons in the group; overrides default selection colors. */
    customTheme?: IRadioButtonBase['customTheme'];
    /** When true, applies error styling to all radio buttons in the group. */
    error?: boolean;
    /**
     * ID of an external element that labels this radio group for accessibility.
     * @deprecated Use `aria-labelledby`. When both are set, `aria-labelledby` wins.
     */
    ariaLabelledby?: string;
    /**
     * HTML id set on the `role="radiogroup"` container, so an `HBFieldControl`
     * label (`control.id`) can target it. When `aria-labelledby` is also set,
     * focusing the container moves focus to the checked radio, or the first
     * enabled one. Without `aria-labelledby` (e.g. the legacy
     * `HBRadioButtonGroupField`) the id is set and nothing else changes.
     * Omitted: no id.
     */
    id?: string;
    /** Accessible name for the radio group when the field has no visible label (e.g. an `HBFieldControl` with `ariaLabel`). Ignored when `aria-labelledby` or `ariaLabelledby` is set. Omitted: no attribute. */
    'aria-label'?: string;
    /** ID of the element that names the radio group (e.g. an `HBFieldControl` label). Omitted: falls back to `ariaLabelledby`. */
    'aria-labelledby'?: string;
    /** Space-separated IDs of hint/error elements describing the radio group. Omitted: no attribute. */
    'aria-describedby'?: string;
    /** Marks the radio group invalid for assistive tech. `aria-invalid="true"` is set when this or `error` is true. Omitted: follows `error` only. */
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
}

export type HBRadioButtonGroupProps<Value extends string | number> =
    _HBRadioButtonGroupProps<Value> & MustIncludeOneOfBasicTestIdProps;

const SPACING: Record<IRadioButtonBase['size'], 8 | 12> = {
    sm: 8,
    md: 8,
    lg: 12
};

const HBRadioButtonGroup = <Value extends string | number>({
    options,
    value,
    onChange,
    name,
    gap = null,
    alignment = 'vertical',
    testId,
    testIdName,
    size = 'md',
    customTheme,
    error,
    ariaLabelledby,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledByProp,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid
}: HBRadioButtonGroupProps<Value> & MustIncludeOneOfBasicTestIdProps) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'radio-button-group',
        componentName: 'HBRadioButtonGroup',
        enforce: false
    });

    const isInvalid = isAriaInvalid(error, ariaInvalid);
    // Only an HBFieldControl-style label (aria-labelledby) needs focus sent to a
    // radio. The legacy field wrappers also pass `id`, and must not change.
    const redirectsFocus = !!(id && ariaLabelledByProp);

    const focusRedirectProps = useGroupFocusRedirect({
        enabled: redirectsFocus,
        getTarget: group => {
            const radios = Array.from(
                group.querySelectorAll<HTMLInputElement>('input[type="radio"]')
            );
            return (
                radios.find(radio => radio.checked) ??
                radios.find(radio => !radio.disabled)
            );
        }
    });

    return (
        <Container
            role="radiogroup"
            id={id}
            {...focusRedirectProps}
            aria-label={
                ariaLabelledByProp || ariaLabelledby ? undefined : ariaLabel
            }
            aria-labelledby={ariaLabelledByProp || ariaLabelledby}
            aria-describedby={ariaDescribedBy}
            aria-invalid={isInvalid || undefined}
            gap={gap || SPACING[size]}
            alignment={alignment}
            data-testid={finalTestId}
        >
            {options.map(({ label, value: optValue, disabled }) => {
                const optionId = optValue.toString();
                return (
                    <HBField
                        isDSNext
                        key={optValue}
                        id={optionId}
                        label={label}
                        placement="right"
                        testIdName={`${testIdName}-${optValue}`}
                        size={size}
                        disabled={disabled}
                    >
                        <HBRadioButton
                            id={optionId}
                            name={name}
                            checked={optValue === value}
                            onChange={onChange}
                            value={optValue}
                            size={size}
                            customTheme={customTheme}
                            disabled={disabled}
                            error={error}
                        />
                    </HBField>
                );
            })}
        </Container>
    );
};

export default HBRadioButtonGroup;

const Container = styled.div<{
    gap: number;
    alignment: 'horizontal' | 'vertical';
}>`
    display: flex;
    flex-direction: ${({ alignment }) =>
        alignment === 'horizontal' ? 'row' : 'column'};
    gap: ${({ gap }) => gap}px;
`;
